import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import { FormikProps } from "formik";
import { TripFormValues, DescriptionSection } from "../types/tripTypes";
import RichTextEditor from "components/shared/TextEditor/RTE";

interface TripSectionsProps {
  formik: FormikProps<TripFormValues>;
  editable?: boolean;
}

const TripSections: React.FC<TripSectionsProps> = ({
  formik,
  editable = true,
}) => {
  const { values, setFieldValue } = formik;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Handle Input Change
  const handleChange = (
    index: number,
    field: keyof DescriptionSection,
    value: string
  ) => {
    const updatedSections = [...values.descriptionSections];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setFieldValue("descriptionSections", updatedSections);
  };

  // Add New Section
  const addSection = () => {
    const newSection = {
      id: values.descriptionSections.length + 1,
      heading: "",
      subHeading: "",
      description: "",
    };
    setFieldValue("descriptionSections", [
      ...values.descriptionSections,
      newSection,
    ]);
    setExpandedIndex(values.descriptionSections.length); // Auto-expand the new section
  };

  // Remove Section
  const removeSection = (index: number) => {
    const updatedSections = values.descriptionSections.filter(
      (_, i) => i !== index
    );
    setFieldValue("descriptionSections", updatedSections);
    
    // Adjust expanded index if necessary
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const getCharacterCount = (text: string = "", maxLength: number) => {
    const remaining = maxLength - text.length;
    const percentage = (text.length / maxLength) * 100;
    const color = percentage > 90 ? "#f44336" : percentage > 75 ? "#ff9800" : "#535862";
    return { remaining, color };
  };

  return (
    <Card 
      sx={{ 
        marginTop: 3, 
        padding: 2, 
        width: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: 2,
        border: "1px solid #E8EAED"
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        <Box sx={{ marginBottom: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 0.5 }}>
            <Typography
              fontWeight={600}
              variant="h6"
              color="#181D27"
              sx={{ fontSize: "20px" }}
            >
              Trip Itinerary
            </Typography>
            <Tooltip 
              title="Organize your trip information into clear, structured sections" 
              arrow
            >
              <ArticleIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
            </Tooltip>
          </Box>
          <Typography 
            variant="body2" 
            color="#535862" 
            sx={{ fontSize: "14px", lineHeight: 1.5 }}
          >
            Create organized sections for day-wise itineraries 
            or any essential information to help travelers make informed decisions.
          </Typography>
        </Box>

        {values.descriptionSections.length === 0 ? (
          <Box
            sx={{
              padding: 4,
              textAlign: "center",
              backgroundColor: "#FAFBFC",
              borderRadius: 2,
              border: "1px solid #E8EAED",
              marginBottom: 3
            }}
          >
            <ArticleIcon sx={{ fontSize: 48, color: "#D1D5DB", marginBottom: 2 }} />
            <Typography color="#6B7280" sx={{ fontSize: "14px" }}>
              No sections added yet. Start by creating your first trip section.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {values.descriptionSections.map((section, index) => {
              const isExpanded = expandedIndex === index;
              const headingCount = getCharacterCount(section.heading, 30);
              const subHeadingCount = getCharacterCount(section.subHeading, 40);

              return (
                <Accordion
                  key={index}
                  expanded={isExpanded}
                  onChange={() => handleExpand(index)}
                  sx={{
                    "&:before": { display: "none" },
                    border: "1px solid #E0E3E7",
                    borderRadius: "12px !important",
                    boxShadow: isExpanded 
                      ? "0 4px 12px rgba(0,0,0,0.1)" 
                      : "0 1px 3px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderColor: "#C1C7CD"
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={
                      <ExpandMoreIcon 
                        sx={{ 
                          color: "#535862",
                          transition: "transform 0.2s ease-in-out"
                        }} 
                      />
                    }
                    sx={{ 
                      margin: 0,
                      padding: "8px 20px",
                      minHeight: "60px",
                      "&.Mui-expanded": {
                        minHeight: "60px"
                      }
                    }}
                  >
                    <Box
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        width: "100%",
                        gap: 2
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="#181D27"
                        fontWeight={600}
                        sx={{ 
                          flexShrink: 0,
                          fontSize: "16px"
                        }}
                      >
                        Day {index + 1}
                      </Typography>

                      {/* Show Heading Preview When Collapsed */}
                      {section.heading && !isExpanded && (
                        <Fade in={!isExpanded} timeout={200}>
                          <Typography
                            variant="body2"
                            color="#6B7280"
                            sx={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                              minWidth: 0
                            }}
                          >
                            {section.heading}
                          </Typography>
                        </Fade>
                      )}

                      {/* Delete Button */}
                      <Tooltip title="Remove this section" arrow>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          disabled={!editable}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSection(index);
                          }}
                          sx={{
                            marginLeft: "auto",
                            color: editable ? "#6B7280" : "#D1D5DB",
                            "&:hover": {
                              color: editable ? "#DC2626" : "#D1D5DB",
                              backgroundColor: editable ? "rgba(220, 38, 38, 0.1)" : "transparent"
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: "0 20px 20px 20px" }}>
                    <Box sx={{ borderTop: "1px solid #F3F4F6", paddingTop: 3 }}>
                      {/* Heading Input */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={500}
                          sx={{
                            marginBottom: 1.5,
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight:'bold'
                          }}
                        >
                          Heading *
                        </Typography>
                        <TextField
                          variant="outlined"
                          fullWidth
                          value={section.heading}
                          disabled={!editable}
                          placeholder="e.g., Day 1 - Arrival & City Tour"
                          onChange={(e) =>
                            handleChange(index, "heading", e.target.value)
                          }
                          inputProps={{ maxLength: 30 }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#9CA3AF"
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3B82F6"
                              }
                            }
                          }}
                        />
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          marginTop: 1 
                        }}>
                          <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                            Keep it short and descriptive
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: headingCount.color,
                              fontWeight: headingCount.remaining < 5 ? 600 : 400
                            }}
                          >
                            {headingCount.remaining} characters left
                          </Typography>
                        </Box>
                      </Box>

                      {/* Subheading Input */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={500}
                          sx={{
                            marginBottom: 1.5,
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight:'bold'
                          }}
                        >
                          Subheading *
                        </Typography>
                        <TextField
                          variant="outlined"
                          fullWidth
                          value={section.subHeading}
                          placeholder="e.g., Explore the historic city center"
                          disabled={!editable}
                          onChange={(e) =>
                            handleChange(index, "subHeading", e.target.value)
                          }
                          inputProps={{ maxLength: 40 }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#9CA3AF"
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3B82F6"
                              }
                            }
                          }}
                        />
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          marginTop: 1 
                        }}>
                          <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                            Add context or brief summary
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: subHeadingCount.color,
                              fontWeight: subHeadingCount.remaining < 5 ? 600 : 400
                            }}
                          >
                            {subHeadingCount.remaining} characters left
                          </Typography>
                        </Box>
                      </Box>

                      {/* Description Input */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={500}
                          sx={{
                            marginBottom: 1.5,
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight:'bold'
                          }}
                        >
                          Description *
                        </Typography>
                        <RichTextEditor
                          value={section.description}
                          onChange={(value) => handleChange(index, "description", value)}
                          placeholder="Provide detailed information about this section. Include activities, timings, locations, and any important details travelers should know..."
                          disabled={!editable}
                          maxLength={1500}
                          minHeight={140}
                        />
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Add New Section Button */}
        {editable && (
          <Fade in={true} timeout={300}>
            <Box
              onClick={addSection}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                color: "#374151",
                fontWeight: 500,
                marginTop: 3,
                border: "2px dashed #D1D5DB",
                borderRadius: "12px",
                padding: "16px 24px",
                minHeight: "56px",
                fontFamily: "Inter, sans-serif",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                backgroundColor: "#FAFBFC",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#9CA3AF",
                  color: "#1F2937"
                },
                "&:active": {
                  transform: "translateY(1px)"
                }
              }}
            >
              <AddIcon sx={{ fontSize: "20px" }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                Add New Section
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Helper Text */}
        {values.descriptionSections.length > 0 && (
          <Box sx={{ 
            marginTop: 3, 
            padding: 2, 
            backgroundColor: "#F0F9FF",
            border: "1px solid #BAE6FD",
            borderRadius: "8px"
          }}>
            <Typography sx={{ 
              fontSize: "13px", 
              color: "#0369A1",
              textAlign: "center"
            }}>
              💡 <strong>Tip:</strong> Use sections for day-wise itineraries, meal information  or activity breakdowns to help travelers understand your trip structure.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TripSections;